import React, { ReactNode, useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { useNavigate } from "react-router";
import { Pages } from "../../utils";
import { useUserContext } from "../../hooks/userContext";
import { useCreateBeforeAndAfterMutation } from "../../hooks/use-create-before-and-after-mutation";
import { useUpdateBeforeAndAfterMutation } from "../../hooks/use-update-before-and-after-mutation";
import { useUpdateMediaFileMutation } from "../../hooks/use-update-media-file-mutation";
import { useNotification } from "../../contexts/NotificationContext";
import BeforeAfterStoryForm from "../../components/before-after-story-form";
import { BeforeAfterBasicInfoForm } from "../../components/before-after-basic-info-form";
import BeforeAfterBlogLayout from "../../components/before-after-blog-layout";
import DashboardHeader from "../../components/dashboard-header";
import { useFirebaseImageUpload } from "../../components/firebase-image-upload/use-firebase-image-upload";
import { FirebasePaths } from "../../utils/firebase";
import { useDashboardFooter } from "../../contexts/DashboardFooterContext";

interface AdditionalSection {
  title: string;
  content: string;
}

interface ImageFile {
  _id: string;
  url: string;
  title?: string;
  description?: string;
}

interface BeforeAfterProjectManagerProps {
  project?: {
    _id: string;
    name: string;
    description: string;
    style: string | null;
    projectCategory: string | null;
    budgetRange: string | null;
    squareFootage: string | null;
    projectDuration: string | null;
    specialRequirements: string[];
    storyTitle: string;
    storySubtitle: string;
    introduction: string;
    challenges: string;
    wishList: string[];
    overview: string;
    additionalSections: AdditionalSection[];
    beforeImages: ImageFile[];
    afterImages: ImageFile[];
    coverImage: ImageFile | null;
    published?: boolean | null;
  };
  isEditMode?: boolean;
}

const SectionCard = ({
  title,
  number,
  required = false,
  children,
}: {
  title: string;
  number?: number;
  required?: boolean;
  children: ReactNode;
}) => (
  <Box
    sx={{
      border: "1px solid #E0E0E0",
      borderRadius: 3,
      p: { xs: 2, md: 3 },
      backgroundColor: "#fff",
    }}
  >
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {number !== undefined && (
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              backgroundColor: "#d81b60",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: "0.875rem",
            }}
          >
            {number}
          </Box>
        )}
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      {required && (
        <Box
          sx={{
            border: "1px solid #4F46E5",
            borderRadius: "999px",
            px: "28px",
            py: "7px",
            fontSize: "0.75rem",
            fontWeight: 600,
            color: "#4F46E5",
            backgroundColor: "#8156FA14",
          }}
        >
          Required
        </Box>
      )}
    </Box>
    {children}
  </Box>
);

export const BeforeAfterProjectManager: React.FC<BeforeAfterProjectManagerProps> = ({
  project,
  isEditMode = false,
}) => {
  const navigate = useNavigate();
  const { user } = useUserContext();
  const { setFooter, clearFooter } = useDashboardFooter();
  const { createBeforeAndAfter, loading: createLoading } = useCreateBeforeAndAfterMutation();
  const { updateBeforeAndAfter, loading: updateLoading } = useUpdateBeforeAndAfterMutation();
  const { updateMediaFile } = useUpdateMediaFileMutation();
  const { showSuccess, showError } = useNotification();
  const { onUpload: firebaseUpload } = useFirebaseImageUpload(FirebasePaths.BEFORE_AND_AFTER);
  
  // Wrapper function to convert MediaFile[] to ImageFile[]
  const onUpload = useCallback(async (files: FileList) => {
    const mediaFiles = await firebaseUpload(files);
    const imageFiles = mediaFiles.map(mediaFile => ({
      _id: mediaFile._id || '',
      url: mediaFile.url || '',
      title: mediaFile.title || undefined,
      description: mediaFile.description || undefined,
      file: undefined // MediaFile doesn't have a file property
    }));
    
    // Add uploaded images to the rich text editor images ref
    // The useEffect will combine these with gallery images
    const existingIds = new Set(richTextEditorImagesRef.current.map(img => img._id));
    const newImages = imageFiles.filter(img => !existingIds.has(img._id));
    richTextEditorImagesRef.current = [...richTextEditorImagesRef.current, ...newImages];
    
    // Trigger update by updating formData
    setFormData(prev => ({
      ...prev,
      availableImages: [
        ...(prev.beforeImages || []),
        ...(prev.afterImages || []),
        ...(prev.coverImage ? [prev.coverImage] : []),
        ...richTextEditorImagesRef.current,
      ],
    }));
    
    return imageFiles;
  }, [firebaseUpload]);

  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCoverUploading, setIsCoverUploading] = useState(false);
  const [isPublished, setIsPublished] = useState<boolean>(() => {
    if (!project) return false;
    return project.published ?? true;
  });
  // Track images uploaded through rich text editor (not in beforeImages/afterImages)
  const richTextEditorImagesRef = useRef<ImageFile[]>([]);
  const prevGalleryIdsRef = useRef<string[]>([]);
  const publishOverrideRef = useRef<boolean | null>(null);

  const cleanAdditionalSections = useCallback(
    (sections: { title?: string; content?: string; __typename?: string }[] = []) =>
      sections.map((section) => ({
        title: section.title || "",
        content: section.content || "",
      })),
    []
  );

  // Form state
  const [formData, setFormData] = useState({
    style: project?.style || null,
    projectCategory: project?.projectCategory || null,
    budgetRange: project?.budgetRange || null,
    squareFootage: project?.squareFootage || null,
    projectDuration: project?.projectDuration || null,
    specialRequirements: project?.specialRequirements || [],
    storyTitle: project?.storyTitle || "",
    storySubtitle: project?.storySubtitle || "",
    introduction: project?.introduction || "",
    challenges: project?.challenges || "",
    wishList: project?.wishList || [],
    overview: project?.overview || "",
    additionalSections: project ? cleanAdditionalSections(project.additionalSections || []) : [],
    beforeImages: project?.beforeImages || [],
    afterImages: project?.afterImages || [],
    coverImage: project?.coverImage || null,
    availableImages: [
      ...(project?.beforeImages || []),
      ...(project?.afterImages || []),
      ...(project?.coverImage ? [project.coverImage] : []),
    ],
  });

  const stripHtml = (value: string) =>
    (value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

  const normalizeSpecialRequirements = useCallback(
    (reqs: (string | null | undefined)[] = []) =>
      reqs
        .filter(Boolean)
        .map((req) => (req as string).replace(/\s+/g, "")),
    []
  );

  const isBasicInfoComplete =
    !!formData.storyTitle.trim() &&
    !!formData.storySubtitle.trim() &&
    !!formData.style &&
    !!formData.projectCategory &&
    !!formData.budgetRange &&
    !!formData.squareFootage &&
    !!formData.projectDuration &&
    !!formData.coverImage;

  const validBeforeImages = (formData.beforeImages || []).filter((img) => img?._id);
  const validAfterImages = (formData.afterImages || []).filter((img) => img?._id);
  const hasBeforeAfterPairs = validBeforeImages.length > 0 && validAfterImages.length > 0;

  const isStoryComplete =
    !!stripHtml(formData.introduction) &&
    !!stripHtml(formData.challenges) &&
    hasBeforeAfterPairs;

  const isFormValid = isBasicInfoComplete && isStoryComplete;


  // Keep availableImages in sync with beforeImages and afterImages
  // This ensures that images uploaded in the first step are immediately available
  // in the rich text editor's image selection dialog
  useEffect(() => {
    const galleryImages = [
      ...(formData.beforeImages || []),
      ...(formData.afterImages || []),
      ...(formData.coverImage ? [formData.coverImage] : []),
    ];
    const byId = new Map<string, ImageFile>();

    galleryImages.forEach((img) => {
      if (img?._id) {
        byId.set(img._id, img);
      }
    });

    richTextEditorImagesRef.current.forEach((img) => {
      if (img?._id && !byId.has(img._id)) {
        byId.set(img._id, img);
      }
    });

    const allAvailableImages = Array.from(byId.values());

    // Only update if the arrays are different to avoid infinite loops
    const currentAvailableIds = (formData.availableImages || []).map(img => img._id).sort().join(',');
    const newAvailableIds = allAvailableImages.map(img => img._id).sort().join(',');
    
    if (currentAvailableIds !== newAvailableIds) {
      setFormData(prev => ({
        ...prev,
        availableImages: allAvailableImages
      }));
    }
  }, [formData.beforeImages, formData.afterImages, formData.coverImage]);

  // Clear cover image if the referenced gallery file was deleted
  useEffect(() => {
    const beforeIds = (formData.beforeImages || []).map((img) => img._id);
    const afterIds = (formData.afterImages || []).map((img) => img._id);
    const galleryIds = [...beforeIds, ...afterIds];
    const cover = formData.coverImage;

    if (cover && cover._id) {
      const coverId = cover._id;
      const wasInGallery = prevGalleryIdsRef.current.includes(coverId);
      const isStillInGallery = galleryIds.includes(coverId);

      if (wasInGallery && !isStillInGallery) {
        setFormData((prev) => ({ ...prev, coverImage: null }));
      }
    }

    prevGalleryIdsRef.current = galleryIds;
  }, [formData.beforeImages, formData.afterImages, formData.coverImage]);

  const handleCoverImageUpload = useCallback(
    async (image: { file: File; local_url: string }) => {
      if (!image?.file) return;
      setIsCoverUploading(true);
      try {
        const dt = new DataTransfer();
        dt.items.add(image.file);
        const mediaFiles = await firebaseUpload(dt.files);

        if (!mediaFiles.length) {
          throw new Error("Failed to upload cover image");
        }

        const uploadedImage = mediaFiles[0];
        const coverImage: ImageFile = {
          _id: uploadedImage._id || "",
          url: uploadedImage.url || "",
          title: uploadedImage.title || undefined,
          description: uploadedImage.description || undefined,
        };

        const existingIds = new Set(richTextEditorImagesRef.current.map((img) => img._id));
        if (!existingIds.has(coverImage._id)) {
          richTextEditorImagesRef.current = [...richTextEditorImagesRef.current, coverImage];
        }

        setFormData((prev) => ({ ...prev, coverImage }));
      } catch (error) {
        showError("Failed to upload cover image. Please try again.");
      } finally {
        setIsCoverUploading(false);
      }
    },
    [firebaseUpload, showError]
  );

  const handleCoverImageRemove = useCallback(() => {
    setFormData((prev) => ({ ...prev, coverImage: null }));
  }, []);

  // Update form data when project changes (for edit mode)
  useEffect(() => {
    if (project && isEditMode) {
      setIsPublished(project.published ?? true);
      setFormData({
        style: project.style || null,
        projectCategory: project.projectCategory || null,
        budgetRange: project.budgetRange || null,
        squareFootage: project.squareFootage || null,
        projectDuration: project.projectDuration || null,
        specialRequirements: project.specialRequirements || [],
        storyTitle: project.storyTitle || "",
        storySubtitle: project.storySubtitle || "",
        introduction: project.introduction || "",
        challenges: project.challenges || "",
        wishList: project.wishList || [],
        overview: project.overview || "",
        additionalSections: cleanAdditionalSections(project.additionalSections || []),
        beforeImages: project.beforeImages || [],
        afterImages: project.afterImages || [],
        coverImage: project.coverImage || null,
        availableImages: [
          ...(project.beforeImages || []),
          ...(project.afterImages || []),
          ...(project.coverImage ? [project.coverImage] : []),
        ],
      });
    }
  }, [project, isEditMode, cleanAdditionalSections]);

  const handleSaveRef = useRef<() => Promise<void>>(async () => {});

  // Keep the save handler stable for footer, while using latest form state via ref.
  useEffect(() => {
    handleSaveRef.current = async () => {
      if (!user?.id) {
        showError("User not found");
        return;
      }
      if (!isFormValid) {
        showError("Please complete all required fields before saving your project.");
        publishOverrideRef.current = null;
        return;
      }

      setIsSaving(true);
      try {
        const imageUpdates = [
          ...(formData.beforeImages || []),
          ...(formData.afterImages || []),
        ].filter((img) => img?._id && img.description);

        if (imageUpdates.length > 0) {
          await Promise.all(
            imageUpdates.map((img) =>
              updateMediaFile({
                _id: img._id,
                title: img.title || null,
                credit: null,
                description: img.description || null,
              })
            )
          );
        }

        const nextPublished = publishOverrideRef.current ?? isPublished;
        const projectData = {
          designerId: user.id,
          name: formData.storyTitle || undefined,
          description: formData.storySubtitle || undefined,
            style: formData.style || null,
            projectCategory: formData.projectCategory || null,
          specialRequirements: normalizeSpecialRequirements(formData.specialRequirements),
          budgetRange: formData.budgetRange || null,
          squareFootage: formData.squareFootage || null,
          projectDuration: formData.projectDuration || null,
          beforeImage: validBeforeImages[0]?._id || null,
          afterImage: validAfterImages[0]?._id || null,
          coverImage: formData.coverImage ? formData.coverImage._id : null,
            storyTitle: formData.storyTitle,
            storySubtitle: formData.storySubtitle,
            introduction: formData.introduction,
            challenges: formData.challenges,
            wishList: formData.wishList,
            overview: formData.overview,
            additionalSections: cleanAdditionalSections(formData.additionalSections),
            beforeImages: validBeforeImages.map((img) => img._id),
            afterImages: validAfterImages.map((img) => img._id),
          published: nextPublished,
          };

        if (isEditMode && project?._id) {
          await updateBeforeAndAfter({
            ...projectData,
            _id: project._id,
          });
          showSuccess("Project updated successfully!");
        } else {
          await createBeforeAndAfter(projectData);
          showSuccess("Project created successfully!");
        }

        if (nextPublished !== isPublished) {
          setIsPublished(nextPublished);
        }

        if (!isEditMode) {
          navigate(Pages.MY_DESIGN_FILES);
        }
      } catch (error) {
        showError("Failed to save project. Please try again.");
      } finally {
        setIsSaving(false);
        publishOverrideRef.current = null;
      }
    };
  }, [
    user?.id,
    isEditMode,
    project,
    formData,
    createBeforeAndAfter,
    updateBeforeAndAfter,
    updateMediaFile,
    showSuccess,
    showError,
    navigate,
    isFormValid,
    cleanAdditionalSections,
    normalizeSpecialRequirements,
    isPublished,
  ]);

  const handleSave = useCallback(() => handleSaveRef.current(), []);
  const handlePublishToggle = useCallback(() => {
    publishOverrideRef.current = !isPublished;
    void handleSaveRef.current();
  }, [isPublished]);

  const handlePreview = useCallback(() => {
    setIsPreviewMode(true);
  }, []);

  const handleEdit = useCallback(() => {
    setIsPreviewMode(false);
  }, []);

  // Form handlers
  const updateFormData = useCallback((updates: Partial<typeof formData>) => {
    setFormData((prev) => {
      let hasChanges = false;
      (Object.keys(updates) as Array<keyof typeof prev>).forEach((key) => {
        if (!Object.is(prev[key], updates[key])) {
          hasChanges = true;
        }
      });

      return hasChanges ? { ...prev, ...updates } : prev;
    });
  }, []);

  const isLoading = createLoading || updateLoading || isSaving;

  useEffect(() => {
    if (isPreviewMode) {
      clearFooter();
      return;
    }

    const footerContent = (
      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2} sx={{ width: "100%" }}>
        <Button
          onClick={handlePublishToggle}
          variant="outlined"
          sx={{
            borderColor: "#000",
            color: "#000",
            px: 4,
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": {
              borderColor: "#333",
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
            "&:disabled": {
              borderColor: "#ccc",
              color: "#666",
            },
          }}
          disabled={isSaving || !isFormValid}
        >
          {isPublished ? "Unpublish" : "Publish"}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            backgroundColor: "#000",
            color: "#fff",
            px: 4,
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "#333",
            },
            "&:disabled": {
              backgroundColor: "#ccc",
              color: "#666",
            },
          }}
          disabled={isSaving || !isFormValid}
        >
          {isEditMode ? "Update Project" : "Add Project"}
        </Button>
      </Stack>
    );

    setFooter(footerContent);

    return () => {
      clearFooter();
    };
  }, [
    setFooter,
    clearFooter,
    handleSave,
    handlePublishToggle,
    isSaving,
    isFormValid,
    isEditMode,
    isPreviewMode,
    isPublished,
  ]);

    if (isPreviewMode) {
      return (
        <Box>
          <BeforeAfterBlogLayout
            project={{
              _id: project?._id || "preview",
              name: formData.storyTitle || formData.storySubtitle || "Before & After Project",
              storyTitle: formData.storyTitle,
              storySubtitle: formData.storySubtitle,
              introduction: formData.introduction,
              challenges: formData.challenges,
              wishList: formData.wishList,
              additionalSections: formData.additionalSections,
              overview: formData.overview,
              beforeImages: formData.beforeImages,
              afterImages: formData.afterImages,
              coverImage: formData.coverImage,
              style: formData.style || undefined,
              projectCategory: formData.projectCategory || undefined,
              budgetRange: formData.budgetRange || undefined,
              squareFootage: formData.squareFootage || undefined,
              projectDuration: formData.projectDuration || undefined,
              specialRequirements: formData.specialRequirements,
            }}
          onEdit={handleEdit}
          showEditButton={true}
          isMember={true}
          showPaywall={false}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <DashboardHeader
        title={isEditMode ? "Edit Before & After Project" : "Create Before & After Project"}
        showBackButton={true}
        primaryAction={{
          label: "Preview Project",
          onClick: handlePreview,
          variant: "contained",
        }}
        primaryActionSx={{
          backgroundColor: "#111",
          color: "#fff",
          borderRadius: "12px",
          px: 3,
          "&:hover": {
            backgroundColor: "#000",
          },
        }}
      />

      <Box sx={{ py: 4, px: { xs: 1, md: 0 } }}>
        <Box
          sx={{
            width: "100%",
            backgroundColor: "#fff",
            borderRadius: 3,
            border: "1px solid #E0E0E0",
            p: { xs: 1, md: 4 },
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
            <Chip
              label={isPublished ? "Published" : "Draft"}
              size="small"
              sx={{
                backgroundColor: isPublished ? "#2E7D32" : "#ED6C02",
                color: "#fff",
                fontWeight: 600,
              }}
            />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {isPublished
                ? "This project is visible in the Designer Files gallery."
                : "Publish when you're ready for this project to appear in the gallery."}
            </Typography>
          </Stack>
          <Stack spacing={4}>
            <SectionCard title="Project Details" number={1} required>
              <BeforeAfterBasicInfoForm
                formData={{
                  projectName: formData.storyTitle,
                  subtitle: formData.storySubtitle,
                  style: formData.style,
                  projectCategory: formData.projectCategory,
                  budgetRange: formData.budgetRange,
                  squareFootage: formData.squareFootage,
                  projectDuration: formData.projectDuration,
                  specialRequirements: formData.specialRequirements,
                }}
                onFormDataChange={(field, value) => {
                  if (field === "projectName") {
                    updateFormData({ storyTitle: value });
                    return;
                  }
                  if (field === "subtitle") {
                    updateFormData({ storySubtitle: value });
                    return;
                  }
                  updateFormData({ [field]: value });
                }}
              />
            </SectionCard>

            <SectionCard title="Build Your Before & After Story" number={2} required>
              <BeforeAfterStoryForm
                formData={{
                  introduction: formData.introduction,
                  challenges: formData.challenges,
                  wishList: formData.wishList,
                  additionalSections: formData.additionalSections,
                  overview: formData.overview,
                  beforeImages: formData.beforeImages,
                  afterImages: formData.afterImages,
                  coverImage: formData.coverImage,
                }}
                onFormDataChange={(field, value) => updateFormData({ [field]: value })}
                onImageUpload={onUpload}
                onCoverImageUpload={handleCoverImageUpload}
                onCoverImageRemove={handleCoverImageRemove}
                isCoverUploading={isCoverUploading}
              />
            </SectionCard>
          </Stack>
        </Box>
      </Box>

      {/* Loading Backdrop */}
      {isLoading && (
        <Backdrop open={isLoading} sx={{ zIndex: 9999 }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <CircularProgress color="inherit" />
            <Typography variant="h6" sx={{ color: "white" }}>
              Saving project...
            </Typography>
          </Box>
        </Backdrop>
      )}
    </Box>
  );
};

export default BeforeAfterProjectManager;
